import React, { useEffect } from 'react';
import { Switch, useParams } from 'react-router-dom';
import { pick, prop } from 'ramda';
import { Route } from 'js/components/common/Route';
import { usePrevious } from 'js/hooks/usePrevious';
import { Redirect } from 'js/components/common/Redirect';
import { storageService } from 'js/services/storage.service';
import { getCleanUrl, isExternalUrl, getPathWithParams } from 'js/utils/url.util';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';

import * as helpers from './helpers';
import * as constants from '../../constants';
import { PageUrlTemplate } from './components/PageUrlTemplate';

const permanentRedirects = (redirects, locale) => {
    if (!redirects) {
        return null;
    }

    return Object.entries(redirects)
        .filter(([fromPage, toPage]) => fromPage?.length > 0 && toPage?.length > 0)
        .map(([fromPage, toPage]) =>
            <Route key={fromPage} path={`${locale}${fromPage}`} exact sensitive>
                <Redirect isReplace to={isExternalUrl(toPage) ? toPage : `${locale}${getPathWithParams(toPage)}`} />
            </Route>
        );
};

const conditionalRedirects = ({
    locale,
    country,
    clientIp,
    rootPage,
    pathLang,
    userLang,
    formFactor,
    localeAlias,
    languagePage,
    forwardRootPage,
    redirectionRules
}) => {
    if (!redirectionRules || !formFactor || !userLang || !clientIp || !country) {
        return null;
    }

    const { ipWhiteList, enable, rulesList = [] } = redirectionRules;
    const isWhiteListedByIp = helpers.isWhiteListed(ipWhiteList, clientIp);

    if (!enable || !rulesList.length || isWhiteListedByIp) {
        return null;
    }

    const redirects = rulesList
        .filter(helpers.platformFilter(formFactor))
        .filter(helpers.scheduleFilter())
        .filter(helpers.countryFilter(country))
        .filter(helpers.languageFilter({ userLang, pathLang, localeAlias }))
        .map(helpers.addPageRegexp);

    return redirects.map(({ fromPages, toPage }) =>
        fromPages
            .filter(fromPage => !helpers.shouldExclude(fromPage, toPage, languagePage))
            .filter(fromPage => !helpers.hasConflictWithForwardRoot({ fromPage, toPage, forwardRootPage, rootPage }))
            .map(fromPage => (
                <Route key={`${fromPage}-${toPage}`} path={`${locale}${fromPage}`} exact>
                    <Redirect isReplace to={isExternalUrl(toPage) ? toPage : `${locale}${getPathWithParams(toPage)}`} />
                </Route>
            ))
    );
};

/**
 * Redirect Router that is responsible for permanent and conditional redirects.
 */
export const RedirectRouter = ({ children }) => {
    let rootPageRedirect;
    const formFactor = useDeviceSelector(prop('formFactor'));
    const {
        redirects,
        redirectionRules,
        redirectionURLTemplates,
        aliasLocaleMapping,
        enableSplashPage,
        splashPage,
        languagePage,
        rootPage,
        forwardRootPage
    } = useAppSelector(pick([
        'redirects',
        'redirectionRules',
        'redirectionURLTemplates',
        'aliasLocaleMapping',
        'enableSplashPage',
        'splashPage',
        'languagePage',
        'rootPage',
        'forwardRootPage'
    ]));
    const { clientIp, country, lang } = useUserSelector(pick(['clientIp', 'country', 'lang']));
    const { pathLang } = useParams();
    const locale = pathLang ? `/${pathLang}` : '';
    const isOnSplash = getCleanUrl() === splashPage;
    const isSplashPrev = usePrevious(isOnSplash);
    const isSplashPageAvailable = splashPage && enableSplashPage;
    const isSplashVisited = storageService.local.getItem(constants.IS_SPLASH_VISITED);
    const { enable } = redirectionRules;

    if (rootPage && redirects[rootPage]) {
        rootPageRedirect = { '/': redirects[rootPage] };
    }

    useEffect(() => {
        if (isSplashPageAvailable && isSplashPrev && !isSplashVisited) {
            storageService.local.setItem(constants.IS_SPLASH_VISITED, true);
        }
    }, [isSplashPageAvailable, isSplashPrev, isSplashVisited]);

    if (isSplashPageAvailable && !isSplashVisited && !isOnSplash && !isSplashPrev) {
        return <Redirect to={splashPage} />;
    }

    if (isSplashPageAvailable && isOnSplash && isSplashVisited) {
        return <Redirect to={rootPage} />;
    }

    return (
        <Switch>
            {permanentRedirects({ ...redirects, ...rootPageRedirect }, locale)}
            {conditionalRedirects({
                clientIp,
                country,
                formFactor,
                forwardRootPage,
                languagePage,
                locale,
                pathLang,
                redirectionRules,
                rootPage,
                userLang: lang,
                localeAlias: aliasLocaleMapping[lang]
            })}
            {redirectionURLTemplates.map((item) => (
                <Route key={item.pageURL} path={`${locale}${item.pageURL}`} exact sensitive>
                    <PageUrlTemplate
                        locale={locale}
                        priority={item.templatePriority}
                        templateType={item.templateType}
                    />
                </Route>
            ))}
            <Route>
                {
                    // conditionalRedirects  should run first, so it's needed to wait until "clientIp" and "country" parameters
                    // present since they participate in conditionalRedirects, unless client redirects are disabled
                    !enable || clientIp !== undefined && country !== undefined
                        ? children
                        : null
                }
            </Route>
        </Switch>
    );
};
